import { getRock5aDeviceSession } from '../services/rock5aDeviceSession.js'

/**
 * lan_discovery.js — LAN 设备发现与共享 Rock5A 会话适配
 *
 * 负责：
 * 1. 通过 discovery_helper HTTP API 发现局域网中的 Rock5a 设备
 * 2. 为旧调用方提供 Rock5aConnection 兼容 API
 * 3. 实际 WebSocket 生命周期由唯一 Rock5aDeviceSession 管理
 */

// =========================================================================
// 设备发现
// =========================================================================

// discovery_helper.py 可能因 Windows WinError 10013 而换端口启动，
// 因此探测一组候选端口（与 Python 端 _FALLBACK_PORTS 保持一致）。
// 顺序：先试常见的"被用户显式指定过的"端口 (9882)，再试默认 8772，
//      缩短命中时间，避免 Windows 把 8772 排除后还要等 5×fetch 超时。
const DISCOVERY_HOST = '127.0.0.1'
const DISCOVERY_PORTS = [9882, 8772, 8773, 18772, 28001]
let _resolvedPort = null  // 缓存已找到的端口

// 允许通过 localStorage 或 URL hash 显式指定 helper 端口 (调试/钉死用)
;(() => {
  try {
    const fromHash = (window.location.hash || '')
      .match(/helperPort=(\d+)/)
    const fromStorage = window.localStorage
      && window.localStorage.getItem('discoveryHelperPort')
    const explicit = (fromHash && fromHash[1]) || fromStorage
    if (explicit && /^\d+$/.test(String(explicit))) {
      const port = parseInt(explicit, 10)
      _resolvedPort = port
      DISCOVERY_PORTS.unshift(port)
      console.log(`[LAN Discovery] 显式 helper 端口: ${port}`)
    }
  } catch (_) { /* ignore */ }
})()

async function _fetchTimeout(url, ms = 1200) {
  const ctrl = new AbortController()
  const timer = setTimeout(() => ctrl.abort(), ms)
  try {
    return await fetch(url, { signal: ctrl.signal })
  } finally {
    clearTimeout(timer)
  }
}

/**
 * 探测 discovery_helper 实际监听的端口（探测 /health）。
 * 命中后缓存，后续直接复用。
 * @returns {Promise<number|null>}
 */
export async function resolveDiscoveryPort() {
  if (_resolvedPort) return _resolvedPort
  const pageProtocol = typeof location !== 'undefined' ? location.protocol : 'unknown:'
  console.log('[LAN Discovery] 开始探测 discovery_helper，候选端口:', DISCOVERY_PORTS,
             '(当前页面协议:', pageProtocol, ')')
  for (const port of DISCOVERY_PORTS) {
    const url = `http://${DISCOVERY_HOST}:${port}/health`
    try {
      const resp = await _fetchTimeout(url, 1200)
      if (resp.ok) {
        _resolvedPort = port
        console.log(`[LAN Discovery] ✅ discovery_helper 命中端口 ${port}`)
        return port
      }
      console.log(`[LAN Discovery] 端口 ${port} HTTP ${resp.status}`)
    } catch (e) {
      // fetch 在 http 页面跨 http 没问题；在 https 页面会被 mixed content 拦截
      // 也可能是端口未监听。打印 cause 帮助排查。
      console.log(`[LAN Discovery] 端口 ${port} 不可用 (${e.name || e.type || 'err'})`)
    }
  }
  console.warn('[LAN Discovery] 所有候选端口均无响应。如果当前是 https:// 页面，'
               + '那很可能是浏览器 mixed-content 把 http:// helper 全部拦了 —— '
               + '请把 vue.config.js 的 devServer.https 改为 false 再测。')
  return null
}

/**
 * 查询局域网中的 Rock5a 设备
 * @returns {Promise<{found: boolean, devices: Array}>}
 */
export async function discoverDevices() {
  const port = await resolveDiscoveryPort()
  if (!port) {
    console.warn('[LAN Discovery] discovery_helper 不可用 (所有候选端口均无响应)')
    return { found: false, devices: [], error: 'helper_unavailable' }
  }
  try {
    const resp = await _fetchTimeout(
      `http://${DISCOVERY_HOST}:${port}/discover`, 2000)
    if (!resp.ok) {
      throw new Error(`Discovery helper returned ${resp.status}`)
    }
    return await resp.json()
  } catch (err) {
    // helper 可能重启并换到候选端口，下一次查询必须重新探测。
    _resolvedPort = null
    console.warn('[LAN Discovery] 查询失败:', err.message)
    return { found: false, devices: [], error: err.message }
  }
}

/**
 * 检查 discovery helper 是否在运行
 * @returns {Promise<boolean>}
 */
export async function isHelperRunning() {
  const port = await resolveDiscoveryPort()
  if (port === null) return false
  try {
    const response = await _fetchTimeout(`http://${DISCOVERY_HOST}:${port}/health`, 1200)
    if (response.ok) return true
  } catch (_) {
    // 继续重新探测；helper 重启后可能换到另一个候选端口。
  }
  _resolvedPort = null
  return (await resolveDiscoveryPort()) !== null
}

// =========================================================================
// WebSocket 连接管理
// =========================================================================

/**
 * Rock5a 连接管理器
 *
 * 这是旧页面使用的兼容适配器。控制通道始终复用全局共享会话，音频通道
 * 只有在 requestAudio() 或注册 onAudioChunk() 后才会按需打开。
 *
 * 事件回调通过 on* 方法注册：
 * - onGesture({gesture, x, y, timestamp, is_active})  — 手势事件
 * - onAudioChunk(ArrayBuffer)                          — 原始 PCM int16 音频帧
 * - onConnected()                                       — 连接建立
 * - onDisconnected()                                    — 连接断开
 */
export class Rock5aConnection {
  constructor(options = {}) {
    this._session = options.session || getRock5aDeviceSession()
    this._owner = options.owner || Symbol('rock5a-connection')
    this._device = null
    this._connected = false
    this._wantsAudio = options.audio === true
    this._unsubscribers = []
    this._lastStatus = this._session.getStatus()

    this._onGesture = null
    this._onAudioChunk = null
    this._onConnected = null
    this._onDisconnected = null
    this._onStatus = null
    this._onTerminalStatus = null
    this._onAuthorizationRequired = null
  }

  get audioWs() { return this._session.audioWs }
  get _audioWs() { return this._session.audioWs }
  get _gestureWs() { return this._session.controlWs }
  get connected() { return this._connected }
  get controlConnected() { return this._lastStatus.control === 'connected' }
  get audioConnected() { return this._lastStatus.audio === 'connected' }
  get ready() { return this._lastStatus.ready }
  get device() { return this._device }
  get status() { return this._session.getStatus() }
  get _reconnectAttempts() { return this.status.reconnectAttempt.control }

  onGesture(fn) { this._onGesture = fn }

  onAudioChunk(fn) {
    this._onAudioChunk = fn
    this._wantsAudio = typeof fn === 'function'
    if (!this._device) return
    if (this._wantsAudio) this._session.requestAudio(this._owner)
    else this._session.releaseAudio(this._owner)
  }

  onConnected(fn) { this._onConnected = fn }
  onDisconnected(fn) { this._onDisconnected = fn }
  onStatus(fn) { this._onStatus = fn }
  onTerminalStatus(fn) { this._onTerminalStatus = fn }
  onAuthorizationRequired(fn) { this._onAuthorizationRequired = fn }

  connect(device) {
    if (!device) throw new Error('[Rock5aConnection] device is required')
    this._device = device
    this._subscribe()
    this._session.setRediscoverHook(async () => await discoverDevices())
    this._session.acquire(device, this._owner, { audio: this._wantsAudio })
    this._handleStatus(this._session.getStatus())
    return this.status
  }

  requestAudio() {
    this._wantsAudio = true
    return this._session.requestAudio(this._owner)
  }

  releaseAudio() {
    this._wantsAudio = false
    this._session.releaseAudio(this._owner)
  }

  sendAudio(data) {
    return this._session.sendAudio(data)
  }

  reconnect(channel = 'control') {
    return this._session.reconnectNow(channel)
  }

  _subscribe() {
    this._unsubscribe()
    this._unsubscribers = [
      this._session.on('gesture', (payload) => {
        if (this._onGesture) this._onGesture(payload)
      }),
      this._session.on('audioChunk', (chunk) => {
        if (this._wantsAudio && this._onAudioChunk) this._onAudioChunk(chunk)
      }),
      this._session.on('terminalStatus', (status) => {
        if (this._onTerminalStatus) this._onTerminalStatus(status)
      }),
      this._session.on('status', (status) => this._handleStatus(status)),
      this._session.on('authorizationRequired', (event) => {
        if (this._onAuthorizationRequired) this._onAuthorizationRequired(event)
      }),
    ]
  }

  _unsubscribe() {
    for (const unsubscribe of this._unsubscribers) unsubscribe()
    this._unsubscribers = []
  }

  _handleStatus(status) {
    this._lastStatus = status
    if (status.device) this._device = status.device
    if (this._onStatus) this._onStatus(status)
    // “终端已连接”只代表共享控制链路在线；音频是否就绪单独暴露，避免
    // 8771 暂时不可用时把整个终端错误标为离线。
    const available = status.control === 'connected'
    this._setConnected(available)
  }

  _setConnected(state) {
    if (this._connected === state) return
    this._connected = state
    if (state) {
      if (this._onConnected) this._onConnected(this.status)
    } else if (this._onDisconnected) {
      this._onDisconnected(this.status)
    }
  }

  disconnect() {
    const wasConnected = this._connected
    this._unsubscribe()
    this._session.release(this._owner)
    this._device = null
    this._connected = false
    this._lastStatus = this._session.getStatus()
    if (wasConnected && this._onDisconnected) this._onDisconnected(this._lastStatus)
  }
}

export function resetDiscoveryPortForTests() {
  _resolvedPort = null
}

export default {
  discoverDevices,
  isHelperRunning,
  resetDiscoveryPortForTests,
  Rock5aConnection,
}
